import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import { agentApi } from "../lib/apiClient";
import { showApiError, showSuccessAction } from "../lib/alerts";
import { asArray, downloadBlob, formatDate, unwrapData } from "../lib/catalog";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";

export default function VisaDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const payload = await agentApi.get(`/travel-partner/bookings/${id}/journey`);
        if (!alive) return;
        setData(unwrapData(payload));
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_VISA");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, [id]);

  async function downloadVisa(visaId, fileName) {
    try {
      const blob = await agentApi.get(`/travel-partner/visas/${visaId}/download`, { responseType: "blob" });
      downloadBlob(blob, fileName || `visa-${visaId}`);
      showSuccessAction("DOWNLOAD_VISA");
    } catch (downloadError) {
      showApiError(downloadError, "DOWNLOAD_VISA");
    }
  }

  const booking = data?.booking || {};
  const pilgrims = asArray(data?.pilgrims);
  const timeline = asArray(data?.timeline);
  const documentRelease = data?.summary?.document_release || {};
  const visasLocked = documentRelease.payment_released !== true;

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Visa detail"
          title={booking.booking_reference || `Booking ${id}`}
          description={
            visasLocked ? (
              <p className="text-secondary small mb-0 mt-2">Issued visa files are locked until this booking payment is complete.</p>
            ) : (
              <p className="text-secondary small mb-0 mt-2">Payment is complete. Issued visa files are released.</p>
            )
          }
          items={[
            DASHBOARD_CRUMB,
            { label: "Services" },
            { label: "Visas", href: "/visas" },
            { label: booking.booking_reference || "Visa Details" },
          ]}
          backTo={true}
          actions={
            <Link to={`/bookings/${id}`} className="btn btn-outline-primary btn-sm">Open booking</Link>
          }
        />

        {loading ? <PageLoader message="Loading visa details..." /> : null}

        {!loading && data ? (
          <div className="row g-4">
            <div className="col-12 col-xl-7">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <h5 className="mb-3">Pilgrim visas</h5>
                  {pilgrims.length > 0 ? pilgrims.map((pilgrim) => (
                    <div className="border rounded-3 p-3 mb-3" key={pilgrim.passenger_id}>
                      <div className="d-flex justify-content-between gap-3">
                        <div>
                          <div className="fw-semibold">{pilgrim.passenger_name}</div>
                          <div className="small text-secondary">Passport: {pilgrim.uppd_passport_no || "Not set"}</div>
                          <div className="small text-secondary">Visa: <StatusBadge value={pilgrim.visa_status || "draft"} /> {pilgrim.visa_number ? `| ${pilgrim.visa_number}` : ""}</div>
                          <div className="small text-secondary">Issued: {formatDate(pilgrim.issued_at)}</div>
                        </div>
                        {pilgrim.visa_id && pilgrim.visa_file_name ? (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm h-100"
                            disabled={visasLocked}
                            onClick={() => downloadVisa(pilgrim.visa_id, pilgrim.visa_file_name)}
                          >
                            {visasLocked ? "Locked until payment" : "Download visa"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  )) : (
                    <div className="text-secondary">No pilgrims returned for this booking.</div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-12 col-xl-5">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body">
                  <h5 className="mb-3">Visa timeline</h5>
                  {timeline.length > 0 ? (
                    <div className="list-group list-group-flush">
                      {timeline.slice(0, 12).map((item) => (
                        <div className="list-group-item px-0" key={item.event_id}>
                          <div className="fw-semibold">{item.event_type}</div>
                          <div className="small text-secondary">{item.notes || "No notes"}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-secondary">No visa events yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}
